import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch} from 'react-redux';
import { Formik, FieldArray } from 'formik';
import { format } from 'date-fns';
import * as yup from 'yup';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Icon,
  TextField,
  Grid,
  Button,
  MenuItem,
  Switch,
  Divider,
  IconButton,
  InputAdornment,
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  TimePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import NumberFormat from 'react-number-format';
import { Breadcrumb, BackdropCircular, ConfirmDialog } from 'components';
import AddLocation from 'components/Wizard/StorePickupLocation/AddLocation';
import {
  getStorePickupLocationsApi,
  deleteStorePickupLocationApi,
  updateStorePickupLocationsApi,
  getStorePickupConfigApi,
  updateStorePickupConfigApi,
  getStoreCustomerGroupsApi
} from 'api';
import {
  addStorePickupLocationStatus,
  triggerStorePickupEnable,
  cancelAddStorePickupLocation
} from 'redux/actions/store_pickup';
import statesArray from '../../components/Wizard/StorePickupLocation/states-array.json';


const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;
const zipRegRxp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Field is required'),
  position: yup
    .number(),
  city: yup
    .string()
    .required('Field is required'),
  postal_code: yup
    .string()
    .required('Field is required')
    .matches(zipRegRxp, 'Please enter a valid zip code (Ex: 90602 or 90602-1234)'),
  state: yup
    .string()
    .required('Field is required'),
  address_line1: yup
    .string()
    .required('Field is required'),
  main_phone: yup
    .string()
    .matches(phoneRegExp, '*Phone number is not valid'),
  email: yup
    .string()
    .email(),
  hours: yup.array()
    .of(
      yup.object().shape({
        from: yup.string().ensure().when('to', {
          is: (to) => to,
          then: yup.string().required('Field is required')
        }),
        to: yup.string().ensure().when('from', {
          is: (from) => from,
          then: yup.string().required('Field is required')
        }),
      }, ['from', 'to']
      )
    )
});

export default function StorePickupLocations() {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(-1);
  const [addNewLocation, setAddNewLocation] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const {
    storePickupLocations = [],
    loadingStorePickupLocations,
    loadingDeleteStorePickupLocation,
    loadingUpdateAddStorePickupLocation,
    loadingAddStorePickupLocation,
    noStorePickupLocations,
    loadingStorePickupConfig,
    isStorePickupEnable,
    storePickupLocationStatus
  } = useSelector((state) => state.store_pickup);

  const {customerGroupsIds = [], loadingCustomerGroups } = useSelector((state) => state.customers);

  const isLoading = loadingStorePickupLocations
    || loadingDeleteStorePickupLocation
    || loadingUpdateAddStorePickupLocation
    || loadingAddStorePickupLocation
    || loadingStorePickupConfig
    || loadingCustomerGroups;

  useEffect(() => {
    if (storePickupLocations.length === 0 && !noStorePickupLocations) {
      getStorePickupLocationsApi({dispatch});
    }

    if (customerGroupsIds.length === 0) {
      getStoreCustomerGroupsApi({dispatch});
    }

    getStorePickupConfigApi({dispatch});
  }, []);

  const handleChangeExpandPanel = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleDeleteLocation = (id) => {
    deleteStorePickupLocationApi({dispatch, id});

    if (storePickupLocations.length <= 1) {
      const configs = [{
        path: 'carriers/pickupatstore/active',
        value: 0
      }];
      updateStorePickupConfigApi({dispatch, configs});
    }
  };

  const handleSaveUpdatedLocation = (values) => {
    const {
      address_line1,
      city,
      country_code,
      customer_group,
      email,
      hours,
      main_phone,
      name,
      place_id,
      position,
      postal_code,
      state,
      status,
      store_code,
      store_id
    } = values;

    const cleanUpEmptyHours = hours.filter((dayHours) => dayHours.from !== null || dayHours.to !== null);
    const location = {
      address_line1,
      city,
      country_code,
      customer_group,
      email,
      hours: cleanUpEmptyHours,
      main_phone,
      name,
      place_id,
      position,
      postal_code,
      state,
      status,
      store_code,
      store_id
    };
    updateStorePickupLocationsApi({dispatch, location});
  };

  const handleAddNewLocation = () => {
    setAddNewLocation(true);
    dispatch(triggerStorePickupEnable(true));
    dispatch(addStorePickupLocationStatus(true));
  };

  const handleCancelAddNewLocation = () => {
    setAddNewLocation(false);
    dispatch(cancelAddStorePickupLocation());
  };

  const handleChangeStorePickupConfig = (event) => {
    const {checked} = event.target;
    const configs = [{
      path: 'carriers/pickupatstore/active',
      value: checked ? 1 : 0
    }];

    if (!storePickupLocations.length) {
      configs[0].value = 0;
      return;
    }

    updateStorePickupConfigApi({dispatch, configs});
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'Store Configurations', path: '/settings#store_configurations' },
            { name: 'Store Pickup' }
          ]}
        />
      </div>
      <div className="m-10">
        <div className="my-10">
          {!addNewLocation &&
            <Button color="primary" variant="contained" onClick={handleAddNewLocation}>
              <Icon>add</Icon>
              Add New Store Pickup Location
            </Button>
          }

          {addNewLocation &&
            <Button
              variant="contained"
              onClick={handleCancelAddNewLocation}
              className="ml-4"
            >
              Cancel
            </Button>
          }
        </div>
        {addNewLocation && storePickupLocationStatus ?
          <AddLocation setAddNewLocation={setAddNewLocation} />
          :
          <div className="my-10">
            <Divider className="mb-10" />
            <Grid container spacing={3} className="flex items-center">
              <Grid item xs={12} sm={4} md={3}>
                Enable Store Pickup:
              </Grid>
              <Grid item xs={12} sm={8} md={9}>
                <Switch
                  color="primary"
                  checked={isStorePickupEnable == 1 && storePickupLocations.length > 0}
                  onChange={(event) => handleChangeStorePickupConfig(event)}
                  onClick={() => {
                    if (!storePickupLocations.length) {
                      setOpenConfirmDialog(true);
                    }
                  }}
                />
              </Grid>
            </Grid>
            <ConfirmDialog
              title="Do you want to enable Store Pickup?"
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                setOpenConfirmDialog(false);
                handleAddNewLocation();
              }}
            >
              You don&apos;t have Store Pickup Locations. Please, add at least one location.
            </ConfirmDialog>
            <Divider className="my-10" />
            <Typography variant="h4" className="py-4">
              {!storePickupLocations.length ?
                'You don\'t have store pickup locations yet'
                : `Your Store Locations (${storePickupLocations.length}):`
              }
            </Typography>
            {!!storePickupLocations.length &&
            storePickupLocations.map((location) => {
              const {
                place_id,
                position,
                name,
                address_line1,
                city,
                postal_code,
                country_code,
                state,
                main_phone,
                email,
                hours,
                store_code,
                store_id,
                status,
                customer_group
              } = location;

              return (
                <Accordion
                  className="p-4"
                  expanded={expanded === `panel_${place_id}`}
                  key={place_id}
                  onChange={handleChangeExpandPanel(`panel_${place_id}`)}
                >
                  <AccordionSummary
                    expandIcon={<Icon>expand_more</Icon>}
                  >
                    <Typography variant="h5">{name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Formik
                      initialValues={{
                        place_id,
                        position,
                        name,
                        address_line1,
                        city,
                        postal_code,
                        country_code,
                        state,
                        main_phone,
                        email,
                        hours: [
                          {
                            to: hours?.find((item) => item.day === 'Monday')?.to || null,
                            from: hours?.find((item) => item.day === 'Monday')?.from || null,
                            day: 'Monday'
                          },
                          {
                            to: hours?.find((item) => item.day === 'Tuesday')?.to || null,
                            from: hours?.find((item) => item.day === 'Tuesday')?.from || null,
                            day: 'Tuesday'
                          },
                          {
                            to: hours?.find((item) => item.day === 'Wednesday')?.to || null,
                            from: hours?.find((item) => item.day === 'Wednesday')?.from || null,
                            day: 'Wednesday'
                          },
                          {
                            to: hours?.find((item) => item.day === 'Thursday')?.to || null,
                            from: hours?.find((item) => item.day === 'Thursday')?.from || null,
                            day: 'Thursday'
                          },
                          {
                            to: hours?.find((item) => item.day === 'Friday')?.to || null,
                            from: hours?.find((item) => item.day === 'Friday')?.from || null,
                            day: 'Friday'
                          },
                          {
                            to: hours?.find((item) => item.day === 'Saturday')?.to || null,
                            from: hours?.find((item) => item.day === 'Saturday')?.from || null,
                            day: 'Saturday'
                          },
                          {
                            to: hours?.find((item) => item.day === 'Sunday')?.to || null,
                            from: hours?.find((item) => item.day === 'Sunday')?.from || null,
                            day: 'Sunday'
                          }
                        ],
                        store_code,
                        store_id,
                        status,
                        customer_group
                      }}
                      validationSchema={validationSchema}
                      onSubmit={handleSaveUpdatedLocation}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        setFieldValue
                      }) => {
                        return (
                          <form
                            className="w-full p-8"
                            type="submit"
                            onSubmit={handleSubmit}
                          >
                            <Typography variant="h6" className="py-4" >General information:</Typography>
                            <TextField
                              required
                              fullWidth
                              className="my-2"
                              name="name"
                              label="Store Title"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.name && Boolean(errors.name)}
                              helperText={touched.name && errors.name}
                            />
                            <TextField
                              fullWidth
                              className="my-2"
                              name="position"
                              label="Store Position"
                              value={values.position}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.position && Boolean(errors.position)}
                              helperText={touched.position && errors.position}
                            />
                            <Typography variant="h6" className="py-4">Store address:</Typography>
                            <TextField
                              required
                              fullWidth
                              className="my-2"
                              name="address_line1"
                              label="Street, suite"
                              value={values.address_line1}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.address_line1 && Boolean(errors.address_line1)}
                              helperText={touched.address_line1 && errors.address_line1}
                            />
                            <TextField
                              required
                              fullWidth
                              className="my-2"
                              name="city"
                              label="City"
                              value={values.city}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.city && Boolean(errors.city)}
                              helperText={touched.city && errors.city}
                            />
                            <TextField
                              required
                              select
                              fullWidth
                              className="my-2"
                              name="state"
                              label="US State"
                              value={values.state}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.state && Boolean(errors.state)}
                              helperText={touched.state && errors.state}
                            >
                              {statesArray.map((state, index) => (
                                <MenuItem value={state.code} key={index}>{state.name}</MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              required
                              fullWidth
                              className="my-2"
                              name="postal_code"
                              label="Postal Code"
                              value={values.postal_code}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.postal_code && Boolean(errors.postal_code)}
                              helperText={touched.postal_code && errors.postal_code}
                            />
                            <Typography variant="h6" className="py-4">Store main contacts:</Typography>
                            <NumberFormat
                              format="+1 (###) ###-####"
                              mask="_"
                              className="w-full"
                              label="Store phone"
                              name="main_phone"
                              placeholder="Store phone number"
                              value={values.main_phone}
                              customInput={TextField}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              InputLabelProps={{ shrink: true }}
                              error={touched.main_phone && Boolean(errors.main_phone)}
                              helperText={touched.main_phone && errors.main_phone}
                            />
                            <TextField
                              fullWidth
                              className="my-2"
                              name="email"
                              label="Store Email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.email && Boolean(errors.email)}
                              helperText={touched.email && errors.email}
                            />
                            <Typography variant="h6" className="py-4">Store working hours:</Typography>
                            <FieldArray name="hours">
                              {() => (
                                <>
                                  {values.hours.map((_, idx) => {
                                    const correctDateValueFrom = values.hours[idx].from ? new Date(`Thu Nov 10 2022 ${values.hours[idx].from}`) : null;
                                    const correctDateValueTo = values.hours[idx].to ? new Date(`Thu Nov 10 2022 ${values.hours[idx].to}`) : null;

                                    return (
                                      <Grid
                                        container
                                        key={`workingDay-${idx}`}
                                        spacing={3}
                                      >
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                          <Grid item xs={12} sm={6}>From:</Grid>
                                          <Grid item xs={12} sm={6}>To:</Grid>
                                          <Grid item xs={12} sm={6}>
                                            <TimePicker
                                              fullWidth
                                              className="my-2 "
                                              name={`hours[${idx}].from`}
                                              label={values.hours[idx].day}
                                              value={correctDateValueFrom}
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    <IconButton
                                                      onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();

                                                        setFieldValue(`hours[${idx}].from`, null);
                                                      }}
                                                    >
                                                      <Icon fontSize="small">
                                                        clear
                                                      </Icon>
                                                    </IconButton>
                                                  </InputAdornment>
                                                ),
                                                placeholder: '--:-- --'
                                              }}
                                              InputLabelProps={{
                                                shrink: true,
                                              }}
                                              onChange={(date) => {
                                                setFieldValue(`hours[${idx}].from`, format(date, 'HH:mm'));
                                              }}
                                              onBlur={handleBlur}
                                              error={errors.hours?.length && Boolean(errors.hours[idx]?.from)}
                                              helperText={errors.hours?.length && errors.hours[idx]?.from}
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            <TimePicker
                                              fullWidth
                                              className="my-2"
                                              name={`hours[${idx}].to`}
                                              label={values.hours[idx].day}
                                              value={correctDateValueTo}
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    <IconButton
                                                      onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();

                                                        setFieldValue(`hours[${idx}].to`, null);
                                                      }}
                                                    >
                                                      <Icon fontSize="small">
                                                        clear
                                                      </Icon>
                                                    </IconButton>
                                                  </InputAdornment>
                                                ),
                                                placeholder: '--:-- --'
                                              }}
                                              InputLabelProps={{
                                                shrink: true,
                                              }}
                                              onChange={(date) => {
                                                setFieldValue(`hours[${idx}].to`, format(date, 'HH:mm'));
                                              }}
                                              onBlur={handleBlur}
                                              error={errors.hours?.length && Boolean(errors.hours[idx]?.to)}
                                              helperText={errors.hours?.length && errors.hours[idx]?.to}
                                            />
                                          </Grid>
                                        </MuiPickersUtilsProvider>
                                      </Grid>
                                    );
                                  })}
                                </>
                              )}
                            </FieldArray>
                            <div className="flex items-center justify-between mt-8">
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleDeleteLocation(place_id)}
                              >
                                Delete Location
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                              >
                                Save Location Changes
                              </Button>
                            </div>
                          </form>
                        );
                      }}
                    </Formik>
                  </AccordionDetails>
                </Accordion>
              );
            })
            }
          </div>
        }
      </div>
      <BackdropCircular loading={isLoading} />
    </div>
  );
}
